import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faAd, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import axiosInstance from '../Datas/AxiosConfig';
import styles from '../Styles/AdBanner.module.scss';

interface Advertisement {
  id: number;
  title: string;
  image_url: string | null;
  content: string | null;
  url: string | null;
  display_interval_minutes: number;
  priority: number;
}

interface AdResponse {
  success: boolean;
  has_banner: boolean;
  banner?: Advertisement;
  message?: string;
}

// ============================================
// Helper Functions (localStorage & consent)
// ============================================

const getAdBannerHistory = () => {
  try {
    const stored = localStorage.getItem('adBannerHistory');
    return stored ? JSON.parse(stored) : { lastShown: 0, lastViewTracked: 0 };
  } catch {
    return { lastShown: 0, lastViewTracked: 0 };
  }
};

const updateAdBannerHistory = (lastShown: number) => {
  const history = {
    lastShown,
    lastViewTracked: Date.now() // Track when we last sent view tracking
  };
  localStorage.setItem('adBannerHistory', JSON.stringify(history));
};

const checkMarketingConsent = (): boolean => {
  const cookiesAccepted = localStorage.getItem('cookiesAccepted');
  const marketingEnabled = localStorage.getItem('marketingEnabled');
  return cookiesAccepted === 'all' || 
         (cookiesAccepted === 'custom' && marketingEnabled === 'true');
};

const shouldShowAdBanner = (displayIntervalMinutes: number): boolean => {
  if (!checkMarketingConsent()) {
    return false;
  }
  
  const history = getAdBannerHistory();
  const intervalMs = displayIntervalMinutes * 60 * 1000;
  const timeSinceLastShown = Date.now() - history.lastShown;
  
  return timeSinceLastShown >= intervalMs;
};

// ============================================
// Main Component
// ============================================

const AdBanner: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [banner, setBanner] = useState<Advertisement | null>(null);

  const serverBaseUrl = "https://xeloba.ge/";
  // const serverBaseUrl = "http://localhost:8000/"
  
  // Refs for stable references
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const statusCheckRef = useRef<NodeJS.Timeout | null>(null);
  const cookieWatcherRef = useRef<NodeJS.Timeout | null>(null);
  const componentMountedRef = useRef<boolean>(true);
  const lastConsentCheckRef = useRef<boolean>(false);
  const currentBannerIdRef = useRef<number | null>(null);
  const viewTrackedRef = useRef<boolean>(false);
  
  // ============================================
  // API Calls
  // ============================================
  
  // Fetch active advertisement
  const fetchActiveAd = useCallback(async (): Promise<void> => {
    if (!checkMarketingConsent() || !componentMountedRef.current) {
      return;
    }

    try {
      const response = await axiosInstance.get<AdResponse>('advertisement/');

      if (response.data.success && response.data.has_banner && response.data.banner) {
        const adBanner = response.data.banner;
        
        // Check if we should show based on interval
        if (shouldShowAdBanner(adBanner.display_interval_minutes)) {
          setBanner(adBanner);
          setIsVisible(true);
          currentBannerIdRef.current = adBanner.id;
          viewTrackedRef.current = false; // Reset view tracking
          
          updateAdBannerHistory(Date.now());
          
          // Track view after banner is shown
          trackBannerView(adBanner.id);
        }
      }
    } catch (err: any) {
      console.error('Error fetching advertisement:', err);
    }
  }, []);

  // Track banner view
  const trackBannerView = useCallback(async (bannerId: number): Promise<void> => {
    if (viewTrackedRef.current) return; // Already tracked
    
    try {
      await axiosInstance.post(`advertisement/${bannerId}/view/`);
      viewTrackedRef.current = true;
      // console.log('Ad view tracked:', bannerId);
    } catch (err) {
      console.error('Error tracking ad view:', err);
    }
  }, []);

  // Track banner click
  const trackBannerClick = useCallback(async (bannerId: number): Promise<string | null> => {
    try {
      const response = await axiosInstance.post(`advertisement/${bannerId}/click/`);
      // console.log('Ad click tracked:', bannerId);
      return response.data.redirect_url || null;
    } catch (err) {
      console.error('Error tracking ad click:', err);
      return null;
    }
  }, []);

  // Check banner status (every 5 minutes)
  const checkBannerStatus = useCallback(async (bannerId: number): Promise<void> => {
    try {
      const response = await axiosInstance.get(`advertisement/${bannerId}/status/`);
      
      if (!response.data.is_active) {
        // console.log('Banner deactivated, hiding...');
        handleClose();
        
        // Stop status checking
        if (statusCheckRef.current) {
          clearInterval(statusCheckRef.current);
          statusCheckRef.current = null;
        }
      }
    } catch (err) {
      console.error('Error checking ad status:', err);
    }
  }, []);

  // ============================================
  // Banner Display Timer
  // ============================================
  
  const startAdTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Check every minute if we should fetch new ad
    timerRef.current = setInterval(() => {
      if (!componentMountedRef.current) return;
      if (!checkMarketingConsent()) return;

      // If currently showing banner, don't fetch
      if (isVisible && banner) return;

      fetchActiveAd();
    }, 60000); // Every minute
  }, [isVisible, banner, fetchActiveAd]);

  // ============================================
  // Status Check Timer (every 5 minutes)
  // ============================================
  
  const startStatusCheckTimer = useCallback((bannerId: number) => {
    if (statusCheckRef.current) {
      clearInterval(statusCheckRef.current);
    }

    // Check status every 5 minutes
    statusCheckRef.current = setInterval(() => {
      if (!componentMountedRef.current) return;
      
      checkBannerStatus(bannerId);
    }, 5 * 60 * 1000); // 5 minutes
  }, [checkBannerStatus]);

  // ============================================
  // Cookie Consent Watcher
  // ============================================
  
  const startCookieWatcher = useCallback(() => {
    if (cookieWatcherRef.current) {
      clearInterval(cookieWatcherRef.current);
    }

    lastConsentCheckRef.current = checkMarketingConsent();

    cookieWatcherRef.current = setInterval(() => {
      if (!componentMountedRef.current) return;
      
      const currentConsent = checkMarketingConsent();
      
      if (currentConsent !== lastConsentCheckRef.current) {
        lastConsentCheckRef.current = currentConsent;
        
        if (currentConsent) {
          setTimeout(() => {
            fetchActiveAd();
          }, 500);
          
          // Stop watching once consent is given
          if (cookieWatcherRef.current) {
            clearInterval(cookieWatcherRef.current);
            cookieWatcherRef.current = null;
          }
        }
      }
    }, 5000); // 5 seconds
  }, [fetchActiveAd]);

  // ============================================
  // Component Lifecycle
  // ============================================
  
  useEffect(() => {
    componentMountedRef.current = true;

    // Initial fetch
    if (checkMarketingConsent()) {
      fetchActiveAd();
    } else {
      startCookieWatcher();
    }

    // Start main timer
    startAdTimer();

    return () => {
      componentMountedRef.current = false;
      
      // Cleanup timers
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      if (statusCheckRef.current) {
        clearInterval(statusCheckRef.current);
        statusCheckRef.current = null;
      }
      
      if (cookieWatcherRef.current) {
        clearInterval(cookieWatcherRef.current);
        cookieWatcherRef.current = null;
      }
    };
  }, [fetchActiveAd, startAdTimer, startCookieWatcher]);

  // Start status check when banner is shown
  useEffect(() => {
    if (isVisible && banner) {
      startStatusCheckTimer(banner.id);
    }
    
    return () => {
      if (statusCheckRef.current) {
        clearInterval(statusCheckRef.current);
        statusCheckRef.current = null;
      }
    };
  }, [isVisible, banner, startStatusCheckTimer]);

  // ============================================
  // Banner Actions
  // ============================================
  
  const handleClose = useCallback(() => {
    setIsVisible(false);
    setBanner(null);
    currentBannerIdRef.current = null;
    viewTrackedRef.current = false;
  }, []);

 const handleBannerClick = useCallback(async () => {
  if (!banner) return;

  // 1️⃣ Track click
  const redirectUrl = await trackBannerClick(banner.id);
  
  // 2️⃣ Close banner
  handleClose();
  
  // 3️⃣ Redirect logic
  if (redirectUrl) {
    // ✅ Check if URL contains "xeloba.ge" or starts with "/"
    const isInternalLink = 
      redirectUrl.includes('xeloba.ge') || 
      redirectUrl.includes('xeloba') ||
      redirectUrl.startsWith('/');
    
    if (isInternalLink) {
      // ✅ Internal navigation - same tab
      if (redirectUrl.startsWith('http')) {
        // Full URL with https://xeloba.ge/...
        const path = new URL(redirectUrl).pathname;
        navigate(path);
      } else {
        // Relative path like "/vip/packages"
        navigate(redirectUrl);
      }
    } else {
      // ✅ External link - new tab
      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
    }
  }
}, [banner, handleClose, navigate, trackBannerClick]);

  // ============================================
  // Render
  // ============================================
  
  // Early return - no unnecessary JSX creation
  if (!isVisible || !banner) {
    return null;
  }

  return (
    <div className={styles.bannerOverlay}>
      <div className={styles.bannerContainer}>
        <div className={styles.banner}>
          {/* Close Button */}
          <button 
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="რეკლამის დახურვა"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          {/* Ad Badge */}
          <div className={styles.adBadge}>
            <FontAwesomeIcon icon={faAd} className={styles.adIcon} />
            <span>სარეკლამო</span>
          </div>

          {/* Header with Title */}
          <div className={styles.header}>
            <h3 className={styles.title}>{banner.title}</h3>
          </div>

          {/* Content */}
          <div className={styles.content}>
            {/* Image */}
            {banner.image_url && (
              <div className={styles.imageContainer}>
                <img 
                  src={banner.image_url}
                  alt={banner.title}
                  className={styles.adImage}
                  loading="lazy"
                />
              </div>
            )}

            {/* Description */}
            {banner.content && (
              <div className={styles.description}>
                <p>{banner.content}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          {banner.url && (
            <div className={styles.actions}>
              <button 
                className={styles.actionButton}
                onClick={handleBannerClick}
              >
                <span>დეტალების ნახვა</span>
                <FontAwesomeIcon icon={faExternalLinkAlt} className={styles.buttonIcon} />
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
});

AdBanner.displayName = 'AdBanner';

export default AdBanner;